/**
 * Whether each Line Items card tab should show the orange flag icon next to the tab label.
 * Mirrors TbFlag visibility in InvoiceLineItemsTab, InvoiceFreightTab, InvoiceMiscellaneousFeesTab, InvoiceIgnoredPartsTab.
 */
import type { InvoiceDetailResponse, NexDetailLine, ReconciliationMatchNote } from '../types/invoice';
import { resolveMatchedNexLineForPartIndex } from './nexLineDisplay';
import { isCreditCardMiscFeeLine } from './invoicePaySettlement';
import { ignoredRowGlobalPartIndex, isIgnoredRowMeaningful } from './returnSlipSubmitPreview';

type LineItemField = keyof InvoiceDetailResponse['lineItems'][0];

function normPartForCompare(value: string | undefined): string {
    return (value ?? '').replace(/[\s-*]/g, '').toLowerCase();
}

export function isNexLineSelectionIssue(i: { issueType?: string } | undefined): boolean {
    const t = i?.issueType;
    return t === 'missing_in_nex' || t === 'different_part_names' || t === 'different_positions' || t === 'differing_qualifiers';
}

export interface InvoiceTabOrangeFlagContext {
    invoice: InvoiceDetailResponse;
    editedFields: Partial<InvoiceDetailResponse>;
    lineItemBackendIndexByCurrentIndex: (number | undefined)[];
    unmatchedPartIndices: Set<number>;
    localMatchedNexOverrides: Record<number, NexDetailLine>;
    flaggedPartIndices: Set<number>;
    effectiveMatchedNexLines?: Array<{ partIndex: number; nexLine: NexDetailLine }>;
    effectiveReconciliationMatchNotes?: ReconciliationMatchNote[];
    getIssuesForPart: (index: number) => any[];
    getLineItemStringValue: (index: number, field: LineItemField, defaultValue?: string) => string;
}

export function invoiceLineItemsTabHasOrangeFlag(ctx: InvoiceTabOrangeFlagContext): boolean {
    const {
        invoice,
        editedFields,
        lineItemBackendIndexByCurrentIndex,
        unmatchedPartIndices,
        localMatchedNexOverrides,
        flaggedPartIndices,
        getIssuesForPart,
        getLineItemStringValue,
    } = ctx;

    const lineItems = editedFields.lineItems ?? invoice?.lineItems ?? [];
    if (lineItems.length === 0) return false;

    const suppressFlagAndSuggestionUi = !!invoice?.reconciliationIssues?.vendorNameMismatch;
    if (suppressFlagAndSuggestionUi) return false;

    const matchedNexLines = ctx.effectiveMatchedNexLines ?? invoice?.reconciliationIssues?.matchedNexLines ?? [];
    const lineItemsFromBackend = invoice?.lineItems ?? [];
    const useDirectIndex =
        lineItems.length === lineItemsFromBackend.length &&
        lineItemBackendIndexByCurrentIndex.length === lineItems.length &&
        lineItemBackendIndexByCurrentIndex.every((bi, i) => bi === i);
    const matchedNexByCurrentIndex: Record<number, NexDetailLine> = {};
    if (useDirectIndex) {
        matchedNexLines.forEach((m) => {
            matchedNexByCurrentIndex[m.partIndex] = m.nexLine;
        });
    } else {
        const matchedNexByBackendIndex: Record<number, NexDetailLine> = Object.fromEntries(
            matchedNexLines.map((m) => [m.partIndex, m.nexLine]),
        ) as Record<number, NexDetailLine>;
        lineItemBackendIndexByCurrentIndex.forEach((backendIdx, currentIdx) => {
            if (backendIdx != null && matchedNexByBackendIndex[backendIdx] !== undefined) {
                matchedNexByCurrentIndex[currentIdx] = matchedNexByBackendIndex[backendIdx];
            }
        });
    }
    unmatchedPartIndices.forEach((idx) => {
        delete matchedNexByCurrentIndex[idx];
    });
    Object.entries(localMatchedNexOverrides).forEach(([idxStr, nexLine]) => {
        const idx = parseInt(idxStr, 10);
        if (!Number.isNaN(idx)) matchedNexByCurrentIndex[idx] = nexLine;
    });

    const baseMatchNotes =
        ctx.effectiveReconciliationMatchNotes ??
        (invoice?.reconciliationIssues as { reconciliationMatchNotes?: typeof ctx.effectiveReconciliationMatchNotes } | undefined)
            ?.reconciliationMatchNotes ??
        [];

    const rejectedInReturnsParts = invoice?.reconciliationIssues?.rejectedInReturnsParts ?? [];

    const rejectedNotes = rejectedInReturnsParts.map((rej) => {
        const currentIdxForRej = useDirectIndex
            ? rej.partIndex
            : lineItemBackendIndexByCurrentIndex.findIndex((bi) => bi === rej.partIndex);
        const item = currentIdxForRej >= 0 ? lineItems[currentIdxForRej] : undefined;
        return {
            partIndex: rej.partIndex,
            invoicePartNumber: item?.partNumber ?? rej.partNumber,
            nexPartNumber: rej.partNumber,
            reason: rej.tooltip ?? 'Part was rejected/returned by the customer (found in NEX returns for this RO).',
        };
    });

    const matchNotes = [
        ...baseMatchNotes,
        ...rejectedNotes.filter(
            (rn) =>
                !baseMatchNotes.some(
                    (bn) =>
                        bn.partIndex === rn.partIndex &&
                        bn.invoicePartNumber === rn.invoicePartNumber &&
                        bn.nexPartNumber === rn.nexPartNumber &&
                        bn.reason === rn.reason
                )
        ),
    ];
    const matchNoteByPartIndex = new Map<number, (typeof matchNotes)[0]>();
    if (useDirectIndex) {
        matchNotes.forEach((n) => matchNoteByPartIndex.set(n.partIndex, n));
    } else {
        lineItemBackendIndexByCurrentIndex.forEach((backendIdx, currentIdx) => {
            if (backendIdx != null) {
                const note = matchNotes.find((n) => n.partIndex === backendIdx);
                if (note) matchNoteByPartIndex.set(currentIdx, note);
            }
        });
    }

    const manualMatchPartIndices = new Set((invoice?.manualMatches ?? []).map((m) => m.partIndex));
    const hasPersistedManualMatchForLineCurrentIndex = (currentIdx: number): boolean => {
        if (useDirectIndex) return manualMatchPartIndices.has(currentIdx);
        const bi = lineItemBackendIndexByCurrentIndex[currentIdx];
        return bi != null && manualMatchPartIndices.has(bi);
    };

    const probableMatchWithoutPnIndices = new Set(
        invoice?.reconciliationIssues?.probableMatchWithoutPartNumber?.partIndices ?? []
    );
    const invoiceStatus = invoice?.status;
    const isSubmittedOrManual = invoiceStatus === 'submitted' || invoiceStatus === 'manually_submitted';

    for (let index = 0; index < lineItems.length; index++) {
        const isUserUnmatched = unmatchedPartIndices.has(index);
        const isFlagged = flaggedPartIndices.has(index) || isUserUnmatched;
        const issues = getIssuesForPart(index);
        const matchedNex = matchedNexByCurrentIndex[index] as
            | { partNumber?: string; partDesc?: string; qtyOnOrder?: number; qty?: number; listPrice?: number; unitCost?: number; extendedCost?: number }
            | undefined;
        const hasMatchedNex = !!matchedNex;
        const userConfirmedNexLineMatch =
            Object.prototype.hasOwnProperty.call(localMatchedNexOverrides, index) || hasPersistedManualMatchForLineCurrentIndex(index);
        const invoicePartNorm = normPartForCompare(getLineItemStringValue(index, 'partNumber', ''));
        const nexPartNorm = normPartForCompare(matchedNex?.partNumber);
        const invoicePartNumberMismatchesNex =
            hasMatchedNex &&
            !!matchedNex?.partNumber &&
            invoicePartNorm.length > 0 &&
            nexPartNorm.length > 0 &&
            invoicePartNorm !== nexPartNorm;
        const probableMatchWithoutPartNumber = probableMatchWithoutPnIndices.has(index);
        const matchNote = matchNoteByPartIndex.get(index);
        const hasMatchNote = !!matchNote;
        const showNexPartNumberConcernForFlag =
            !userConfirmedNexLineMatch &&
            (invoicePartNumberMismatchesNex || probableMatchWithoutPartNumber) &&
            !(probableMatchWithoutPartNumber && hasMatchedNex) &&
            !(isSubmittedOrManual && hasMatchedNex && invoicePartNumberMismatchesNex) &&
            !(hasMatchNote && hasMatchedNex && issues.length === 0);
        const issuesForMatchedRowFlag = userConfirmedNexLineMatch
            ? issues.filter((i: { issueType?: string }) => !isNexLineSelectionIssue(i))
            : issues;

        const showsOrangeFlag =
            (!hasMatchedNex && isUserUnmatched) ||
            (!hasMatchedNex && !isUserUnmatched && isFlagged && issues.length > 0) ||
            (hasMatchedNex && ((isFlagged && issuesForMatchedRowFlag.length > 0) || showNexPartNumberConcernForFlag));

        if (showsOrangeFlag) return true;
    }

    return false;
}

export function invoiceFreightTabHasOrangeFlag(ctx: InvoiceTabOrangeFlagContext): boolean {
    const { invoice, editedFields, unmatchedPartIndices, localMatchedNexOverrides, flaggedPartIndices, getIssuesForPart } = ctx;

    const lineItems = editedFields.lineItems ?? invoice?.lineItems ?? [];
    const freightItems = editedFields.freightItems ?? invoice?.freightItems ?? [];
    if (freightItems.length === 0) return false;

    const suppressFlagAndSuggestionUi = !!invoice?.reconciliationIssues?.vendorNameMismatch;
    if (suppressFlagAndSuggestionUi) return false;

    const matchedNexLines = ctx.effectiveMatchedNexLines ?? invoice?.reconciliationIssues?.matchedNexLines ?? [];

    for (let index = 0; index < freightItems.length; index++) {
        const partIndexGlobal = lineItems.length + index;
        const freightIssuePartIndex = -(index + 1);

        const matchedNex = resolveMatchedNexLineForPartIndex(
            partIndexGlobal,
            matchedNexLines,
            localMatchedNexOverrides,
            unmatchedPartIndices,
        );
        const hasMatchedNex = !!matchedNex;

        const isUserUnmatched = unmatchedPartIndices.has(partIndexGlobal);
        const isFlagged =
            flaggedPartIndices.has(partIndexGlobal) || flaggedPartIndices.has(freightIssuePartIndex) || isUserUnmatched;
        const issues = getIssuesForPart(freightIssuePartIndex);

        if (!hasMatchedNex && isUserUnmatched) return true;
        if (!hasMatchedNex && !isUserUnmatched && isFlagged && issues.length > 0) return true;
    }

    return false;
}

export function invoiceMiscFeesTabHasOrangeFlag(ctx: InvoiceTabOrangeFlagContext): boolean {
    const { invoice, editedFields, unmatchedPartIndices, localMatchedNexOverrides, flaggedPartIndices, getIssuesForPart } = ctx;
    const items = editedFields.restockingFeeItems ?? invoice?.restockingFeeItems ?? [];
    const lineItems = editedFields.lineItems ?? invoice?.lineItems ?? [];
    const freightItems = editedFields.freightItems ?? invoice?.freightItems ?? [];
    const fuelItems = editedFields.fuelItems ?? invoice?.fuelItems ?? [];
    const payCharge = (editedFields.paySettlementType ?? invoice?.paySettlementType) === 'charge';
    const suppressFlagAndSuggestionUi = !!invoice?.reconciliationIssues?.vendorNameMismatch;
    if (suppressFlagAndSuggestionUi) return false;
    const matchedNexLines = ctx.effectiveMatchedNexLines ?? invoice?.reconciliationIssues?.matchedNexLines ?? [];
    const visibleIndices = items
        .map((item, idx) => (payCharge && isCreditCardMiscFeeLine(item) ? null : idx))
        .filter((idx): idx is number => idx !== null);
    for (const index of visibleIndices) {
        const partIndexGlobal = lineItems.length + freightItems.length + fuelItems.length + index;
        const legacyRestockingIssuePartIndex = -(index + 1);
        const restockingFeeIssue = invoice?.reconciliationIssues?.flaggedParts?.find(
            (issue) => issue.issueType === 'restocking_fee_on_return' && issue.partIndex === legacyRestockingIssuePartIndex
        );
        const matchedNex = resolveMatchedNexLineForPartIndex(
            partIndexGlobal,
            matchedNexLines,
            localMatchedNexOverrides,
            unmatchedPartIndices,
        );
        const hasMatchedNex = !!matchedNex;
        const isUserUnmatched = unmatchedPartIndices.has(partIndexGlobal);
        const isFlagged =
            flaggedPartIndices.has(partIndexGlobal) ||
            flaggedPartIndices.has(legacyRestockingIssuePartIndex) ||
            isUserUnmatched ||
            !!restockingFeeIssue;
        const issues = getIssuesForPart(partIndexGlobal);
        if (!hasMatchedNex && isUserUnmatched) return true;
        if (!hasMatchedNex && !isUserUnmatched && (issues.length > 0 || !!restockingFeeIssue) && isFlagged) return true;
    }
    return false;
}

export function invoiceReturnSlipIgnoredTabHasOrangeFlag(ctx: InvoiceTabOrangeFlagContext): boolean {
    const {
        invoice,
        editedFields,
        unmatchedPartIndices,
        localMatchedNexOverrides,
        flaggedPartIndices,
        getIssuesForPart,
    } = ctx;

    const suppressFlagAndSuggestionUi = !!invoice?.reconciliationIssues?.vendorNameMismatch;
    if (suppressFlagAndSuggestionUi) return false;

    const ignoredList = editedFields.ignoredParts ?? invoice?.ignoredParts ?? [];
    const matchedNexForIgnored = invoice?.reconciliationIssues?.matchedNexLinesForIgnored ?? [];
    const matchedNexByIgnoredIndex = Object.fromEntries(
        matchedNexForIgnored.map((m) => [m.ignoredIndex, m.nexLine]),
    ) as Record<number, NexDetailLine>;
    const lineItemsForGlobal = editedFields.lineItems ?? invoice?.lineItems ?? [];
    const freightForGlobal = editedFields.freightItems ?? invoice?.freightItems ?? [];
    const fuelForGlobal = editedFields.fuelItems ?? invoice?.fuelItems ?? [];
    const restockingForGlobal = editedFields.restockingFeeItems ?? invoice?.restockingFeeItems ?? [];
    const discountForGlobal = editedFields.discountItems ?? invoice?.discountItems ?? [];
    const matchedNexLinesAll = ctx.effectiveMatchedNexLines ?? invoice?.reconciliationIssues?.matchedNexLines ?? [];
    const manualMatchPartIndices = new Set((invoice?.manualMatches ?? []).map((m) => m.partIndex));

    for (let index = 0; index < ignoredList.length; index++) {
        const ignoredRow = ignoredList[index];
        const g = isIgnoredRowMeaningful(ignoredRow)
            ? ignoredRowGlobalPartIndex({
                  ignoredIndex: index,
                  lineItemsLength: lineItemsForGlobal.length,
                  freightItemsLength: freightForGlobal.length,
                  fuelItemsLength: fuelForGlobal.length,
                  restockingItemsLength: restockingForGlobal.length,
                  discountItemsLength: discountForGlobal.length,
              })
            : -1;

        const fromIgnoredMap = matchedNexByIgnoredIndex[index];
        let matchedNex: NexDetailLine | undefined =
            g >= 0
                ? resolveMatchedNexLineForPartIndex(g, matchedNexLinesAll, localMatchedNexOverrides, unmatchedPartIndices)
                : fromIgnoredMap;
        if (matchedNex == null && fromIgnoredMap != null) {
            matchedNex = fromIgnoredMap;
        }
        const hasMatchedNex = !!matchedNex;
        const userConfirmedNexLineMatch =
            g >= 0 &&
            (Object.prototype.hasOwnProperty.call(localMatchedNexOverrides, g) || manualMatchPartIndices.has(g));
        const isUserUnmatched = g >= 0 && unmatchedPartIndices.has(g);
        const issues = g >= 0 && isIgnoredRowMeaningful(ignoredRow) ? getIssuesForPart(g) : [];
        const isFlagged = g >= 0 && isIgnoredRowMeaningful(ignoredRow) && (flaggedPartIndices.has(g) || isUserUnmatched);
        const issuesForMatchedRowFlag = userConfirmedNexLineMatch
            ? issues.filter((i: { issueType?: string }) => !isNexLineSelectionIssue(i))
            : issues;

        if (g < 0 || !isIgnoredRowMeaningful(ignoredRow)) continue;

        if (!hasMatchedNex && isUserUnmatched) return true;
        if (!hasMatchedNex && !isUserUnmatched && isFlagged && issues.length > 0) return true;
        if (hasMatchedNex && isFlagged && issuesForMatchedRowFlag.length > 0) return true;
    }

    return false;
}
